import React from 'react';
import { Box, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { useIntl } from 'react-intl';
import SectionLabel from '../../SectionLabel';
import useTranslate from '../../../Hooks/useTranslate';
import { WIDGET_TYPES } from '../detail/WidgetRegistry';

/**
 * One of the widgets, as a line to drag onto the canvas or to pick.
 *
 * @param {Object} props - Component props.
 * @param {String} props.widget - The widget's name, which is what a drop carries.
 * @param {String} props.label - The wording shown.
 * @param {Function} props.onPick - Takes it as chosen; dragging is offered instead when absent.
 * @param {Function} props.onRemove - Takes the preset off, for the ones that are one.
 *
 * @returns {React.JSX.Element}
 */
const PaletteRow = function PaletteRow({
  widget,
  label,
  onPick = null,
  onRemove = null,
}) {
  const intl = useIntl();
  const translate = useTranslate();

  return (
    <Paper
      variant="outlined"
      draggable={!onPick}
      unselectable="on"
      onDragStart={
        onPick ? undefined : (e) => e.dataTransfer.setData('text/plain', widget)
      }
      onClick={onPick ? () => onPick(widget) : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        p: '8px 12px',
        cursor: onPick ? 'pointer' : 'grab',
        borderRadius: '10px',
        '&:active': { cursor: onPick ? 'pointer' : 'grabbing' },
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      {!onPick && (
        <DragIndicatorRoundedIcon fontSize="small" sx={{ opacity: 0.5 }} />
      )}
      <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
        {translate(label)}
      </Typography>
      {onRemove && (
        <Tooltip arrow title={intl.messages.DELETE_PRESET}>
          <IconButton
            id={`remove-preset-${widget}`}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <DeleteRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Paper>
  );
};

/**
 * The widgets a region can be: on the page, the ones to drag onto the canvas; inside a container,
 * the ones that container allows.
 *
 * Dragging is how one joins the page, because where it goes is part of what is being decided and a
 * widget that appears somewhere of its own choosing has to be moved before it can be judged. Inside
 * a container it is picked instead: a child fills its container, so there is nowhere to drop it.
 *
 * The saved presets are listed apart from the widgets checked in. They are added the same way - a
 * preset is a widget as far as a layout is concerned - but they can also be taken off, which is only
 * true of the ones somebody saved.
 *
 * @param {Object} props - Component props.
 * @param {Array} props.widgets - What to offer; every registered widget when not given.
 * @param {Function} props.onPick - Receives the widget chosen; dragging is offered when absent.
 * @param {Function} props.onRemovePreset - Receives the id of the preset to take off.
 *
 * @returns {React.JSX.Element}
 */
const WidgetPalette = function WidgetPalette({
  widgets = null,
  onPick = null,
  onRemovePreset = null,
}) {
  const intl = useIntl();

  const offered = widgets ?? WIDGET_TYPES;
  const presets = offered.filter(({ presetId }) => presetId);
  const checkedIn = offered.filter(({ presetId }) => !presetId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {checkedIn.map(({ widget, label }) => (
        <PaletteRow
          key={widget}
          widget={widget}
          label={label}
          onPick={onPick}
        />
      ))}
      {presets.length > 0 && (
        <>
          <SectionLabel>{intl.messages.PRESETS}</SectionLabel>
          {presets.map(({ widget, label, presetId }) => (
            <PaletteRow
              key={widget}
              widget={widget}
              label={label}
              onPick={onPick}
              onRemove={
                onRemovePreset ? () => onRemovePreset(presetId) : undefined
              }
            />
          ))}
        </>
      )}
    </Box>
  );
};

export default WidgetPalette;
